import toast from 'react-hot-toast'

import type { UpdateTrackStatusDTO } from '@fv/client-components'
import {
  buildFetchOptionsWithAuth,
  fetchJson,
  useMutation,
  useQueryClient,
} from '@fv/client-core'
import type { ListShipment } from '@fv/client-types'
import { fvDate } from '@fv/dates'

import { apiUri, supportMessage } from '@/constants'
import { useUpdateCachedLoad } from '@/hooks/shipments'

import { trackingKeys } from './queries'

async function updateTrackStatus(
  loadId: string,
  dto: UpdateTrackStatusDTO,
): Promise<ListShipment> {
  const { eventDate, eventTime, message, sequence } = dto
  const endpoint = `${apiUri}/shipments/${loadId}/track/current-stop`
  const options = buildFetchOptionsWithAuth({
    body: JSON.stringify({
      sequence,
      ...(eventDate && { eventDate: fvDate(eventDate).format('YYYY-MM-DD') }),
      ...(eventTime && { eventTime }),
      ...(message?.trim() && { message: message.trim() }),
    }),
    method: 'PUT',
  })

  const response = await fetchJson(endpoint, options)
  if (response.ok) return response.json
  throw response.errorMessage
}

export function useUpdateTrackStatus(loadId: string) {
  const queryClient = useQueryClient()
  const updateCachedLoad = useUpdateCachedLoad()

  return useMutation(
    (dto: UpdateTrackStatusDTO) => updateTrackStatus(loadId, dto),
    {
      onSettled: (updatedLoad, error) => {
        if (error) {
          toast.error(`Unable to update tracking, ${supportMessage}`)
          return
        }

        if (updatedLoad) updateCachedLoad({ updatedLoad })
        queryClient.invalidateQueries({ queryKey: trackingKeys.load(loadId) })
        toast.success('Tracking updated successfully.')
      },
    },
  )
}
